import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { RecentPost } from "@/lib/api/interfaces/posts.interface";
import { usePostsGetRecentInsights } from "@/lib/api/requests/posts.requests";
import {
  DataView,
  DataViewLayout,
  DataViewContent,
  DataViewLoadingState,
  DataViewEmptyState,
  DataViewLoadMore,
  DataViewGridCard,
  DataViewListCard,
  DataViewToolbar,
  GenericItem,
  GenericItemActions,
  EntityPreviewFrame
} from "@/util-components/data-view";
import { AdvancedFilterDef, AdvancedSortDef, AdvancedState } from "@/util-components/data-view/components/advanced-filter-types";
import { PostOrigin, PostType } from "@/lib/api/interfaces/posts.interface";
import { PageInfoBanner, SubPageLayout } from "@/util-components/page-layout";
import { usePageState } from "@/lib/hooks/use-page-state";
import { History, LayoutTemplate, Instagram, Facebook, FileText, MessageCircle } from "lucide-react";
import { formatFriendlyDate } from "@/lib/utils/date-formatter";
import { getSocialIcon } from "@/page-components/post-generator/utils/socialUtils";
import { RecentPostDialog } from "../components/RecentPostDialog";

const DEFAULT_PAGE_SIZE = 18;

const filterDefs: AdvancedFilterDef[] = [
  {
    key: "origin",
    label: "Plattform",
    type: "select",
    icon: LayoutTemplate,
    options: [
      { value: PostOrigin.INSTAGRAM, label: "Instagram", icon: Instagram },
      { value: PostOrigin.FACEBOOK, label: "Facebook", icon: Facebook },
      { value: PostOrigin.WHATSAPP, label: "WhatsApp", icon: MessageCircle },
    ]
  },
  {
    key: "type",
    label: "Typ",
    type: "select",
    icon: FileText,
    options: [
      { value: PostType.POST, label: "Post" },
      { value: PostType.STORY, label: "Story" },
    ]
  }
];

const sortDefs: AdvancedSortDef[] = [
  { key: 'createdAt', label: 'Erstellt am' }
];

const EmptyPreview = () => (
  <div className="flex h-full w-full items-center justify-center text-gray-300">
    <LayoutTemplate className="h-9 w-9" />
  </div>
);

const StatusLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[11px] font-medium text-gray-500">{children}</span>
);

const PlatformPill = ({ children }: { children: React.ReactNode }) => (
  <div className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500">
    {children}
  </div>
);

const PastPosts: React.FC = () => {
  const navigate = useNavigate();
  const { state, setInfoBannerVisible, setViewMode, setSettings } = usePageState<AdvancedState>('post-generator-past-posts', {
    isInfoBannerVisible: true,
    viewMode: 'grid',
    settings: {
      filters: [],
      sorts: [{ key: 'createdAt', direction: 'desc' }]
    }
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPosts, setAllPosts] = useState<RecentPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<RecentPost | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  
  const viewMode = state.viewMode || 'grid';
  const filters = state.settings?.filters || [];
  const sorts = state.settings?.sorts || [{ key: 'createdAt', direction: 'desc' }];
  const currentSort = sorts[0];

  // Extract specific filter values
  const typeFilter = filters.find(f => f.key === 'type')?.value as string | undefined;
  const originFilter = filters.find(f => f.key === 'origin')?.value as string | undefined;

  const recentQuery = usePostsGetRecentInsights({
    page: currentPage - 1,
    limit: DEFAULT_PAGE_SIZE,
    q: searchTerm,
    type: typeFilter,
    origin: originFilter,
    sort: currentSort?.key,
    dir: currentSort?.direction
  }, { enabled: true });

  useEffect(() => {
    if (recentQuery.data?.docs) {
      if (currentPage === 1) {
        setAllPosts(recentQuery.data.docs);
      } else {
        setAllPosts(prev => {
          const newPosts = recentQuery.data.docs.filter((p: RecentPost) => !prev.find(e => e._id === p._id));
          return [...prev, ...newPosts];
        });
      }
    }
  }, [recentQuery.data, currentPage]);

  const hasMorePosts = recentQuery.data?.hasNextPage || false;
  
  // Prevent empty state flash
  const isSyncing = (recentQuery.data?.docs?.length ?? 0) > 0 && allPosts.length === 0;
  const isInitialLoading = (recentQuery.isLoading || isSyncing) && currentPage === 1;
  const isLoadingMore = recentQuery.isLoading && currentPage > 1;

  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
  };

  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    setCurrentPage(1);
    setAllPosts([]);
  };

  const handlePostClick = (postId: string) => {
    const post = allPosts.find(p => p._id === postId);
    if (post) {
      setSelectedPost(post);
      setShowDialog(true);
    }
  };


  const convertToGenericItem = (r: RecentPost): GenericItem => {
    return {
      id: r._id,
      title: r.message ? r.message.substring(0, 30) + (r.message.length > 30 ? '...' : '') : (r.template?.name || 'Veröffentlichter Post'),
      previewContent: (
        <EntityPreviewFrame backgroundUrl={r.thumbnailUrl || r.template?.thumbnailUrl}>
          {r.thumbnailUrl ? (
             <img src={r.thumbnailUrl} alt="Vorschau" className="max-h-full max-w-full rounded-sm object-contain" loading="lazy" />
          ) : r.template?.thumbnailUrl ? (
             <img src={r.template.thumbnailUrl} alt="Vorschau" className="max-h-full max-w-full rounded-sm object-contain" loading="lazy" />
          ) : (
             <EmptyPreview />
          )}
        </EntityPreviewFrame>
      ),
      metadata: {
        badgeText: "Gepostet",
        footer: (
          <StatusLabel>{formatFriendlyDate(r.createdAt)}</StatusLabel>
        ),
        platforms: [
          r.origin ? (
            <PlatformPill key="origin">
              {getSocialIcon(r.origin, "h-3.5 w-3.5 text-gray-500")}
            </PlatformPill>
          ) : (
             <PlatformPill key="placeholder">
                <LayoutTemplate className="w-3.5 h-3.5" />
             </PlatformPill>
          )
        ]
      }
    };
  };

  const genericItems = useMemo(() => {
    return allPosts.map(convertToGenericItem);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPosts]);

  const genericActions: GenericItemActions = {
    onItemClick: (id: string) => {
      handlePostClick(id);
    }
  };

  const handleLoadMore = () => {
    if (hasMorePosts && !isLoadingMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <SubPageLayout 
      pageTitle="Vergangene Posts" 
      icon={<History />} 
      navigateBackCallback={() => navigate(-1)}
    >
      <DataView 
        viewMode={viewMode} 
        onViewModeChange={handleViewModeChange}
        isLoading={isInitialLoading}
        isEmpty={allPosts.length === 0}
      >
        <div className="bg-white flex-shrink-0 border-b border-gray-100">
          {state.isInfoBannerVisible && (
            <div className="p-4 pb-0">
              <PageInfoBanner
                title="Dein Post-Archiv"
                description="Hier siehst du alle Posts, die in der Vergangenheit über SportyPost veröffentlicht wurden."
                icon={History}
                variant="blue"
                onClose={() => setInfoBannerVisible(false)}
              />
            </div>
          )}
          
          <DataViewToolbar 
            searchQuery={searchTerm}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Archiv durchsuchen..."
            infoText={`${recentQuery.data?.count || 0} vergangene Posts`}
            
            mode="advanced"
            advancedFilterDefs={filterDefs}
            advancedSortDefs={sortDefs}
            advancedState={{
              filters: state.settings?.filters || [],
              sorts: state.settings?.sorts || [{ key: 'createdAt', direction: 'desc' }]
            }}
            onAdvancedStateChange={(newState) => {
              setSettings(newState);
              setCurrentPage(1); // Reset page on filter change
              setAllPosts([]); // Clear posts to avoid mixing filtered results
            }}
            onAdvancedReset={() => {
              setSettings({
                filters: [],
                sorts: [{ key: 'createdAt', direction: 'desc' }]
              });
              setCurrentPage(1);
              setAllPosts([]);
            }}
            defaultSort={{ key: 'createdAt', direction: 'desc' }}
          />
        </div>

      <DataViewLayout>
        <DataViewContent
           items={genericItems}
           renderGridItem={(item) => <DataViewGridCard item={item} actions={genericActions} />}
           renderListItem={(item) => <DataViewListCard item={item} actions={genericActions} />}
           loadingIndicator={<DataViewLoadingState />}
           emptyState={
             <DataViewEmptyState
               icon={<History className="h-8 w-8 text-gray-400" />}
               title={searchTerm ? 'Keine Posts gefunden' : 'Keine vergangenen Posts gefunden'}
               description={searchTerm ? 'Versuchen Sie andere Suchbegriffe.' : 'Du hast noch keine Posts über SportyPost veröffentlicht.'}
             />
           }
        />

        <DataViewLoadMore
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore}
          hasMore={hasMorePosts}
        />
      </DataViewLayout>
        
      <RecentPostDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        post={selectedPost}
      />

      </DataView>
    </SubPageLayout>
  );
};

export default PastPosts;
